import Offcanvas from 'bootstrap/js/dist/offcanvas';
import ky from 'ky';
import { t } from './i18n';
import { minQtyForOptions, minQtyFromDataset } from './order-rules';

interface AddToCartResponse {
  id: string;
  quantity: number;
  variant_id: string;
}

interface ErrorResponse {
  message?: string;
  description?: string;
}

interface ProductVariant {
  id: number;
  title: string;
  price: number;
  compare_at_price: number | null;
  available: boolean;
  option1: string | null;
  option2: string | null;
  option3: string | null;
}

interface ProductJson {
  title: string;
  featured_image: string | null;
  variants: ProductVariant[];
}

class ProductForm {
  private variantPickerOffcanvas: Offcanvas | null = null;

  constructor() {
    this.init();
  }

  init(): void {
    this.bindEvents();
  }

  private getFormRoot(el: HTMLElement): HTMLElement | null {
    return el.closest('.product-form, .cookie-product-form') as HTMLElement | null;
  }

  bindEvents(): void {
    document.addEventListener('submit', (e: Event) => {
      const target = e.target as HTMLElement;
      if (target.closest('form[action*="/cart/add"]')) {
        e.preventDefault();
        this.handleAddToCart(target as HTMLFormElement);
      }
    });

    document.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement;

      const variantPickerTrigger = target.closest('.variant-picker-trigger') as HTMLElement;
      if (variantPickerTrigger) {
        e.preventDefault();
        const handle = variantPickerTrigger.dataset.productHandle;
        if (handle) {
          this.openVariantPicker(handle);
        }
        return;
      }

      const qtyPlus = target.closest('.qty-plus') as HTMLElement | null;
      const qtyMinus = target.closest('.qty-minus') as HTMLElement | null;
      if (qtyPlus && this.getFormRoot(qtyPlus)) {
        this.handleQuantityChange(qtyPlus, 'increase');
      } else if (qtyMinus && this.getFormRoot(qtyMinus)) {
        this.handleQuantityChange(qtyMinus, 'decrease');
      }
    });

    document.addEventListener('change', (e: Event) => {
      const target = e.target as HTMLSelectElement;
      if (target.classList.contains('variant-selector')) {
        this.handleVariantChange(target);
      }
    });

    document.addEventListener('input', (e: Event) => {
      const target = e.target as HTMLInputElement;
      if (target.classList.contains('quantity-input')) {
        this.validateQuantityInput(target);
        const form = this.getFormRoot(target);
        if (form) {
          this.refreshProductPrice(form);
        }
      }
    });
  }

  async handleAddToCart(form: HTMLFormElement): Promise<void> {
    const isVariantPickerForm = !!form.closest('#offcanvas-variant-picker');

    try {
      this.setLoadingState(form, true);
      this.hideError(form);

      const formData = new FormData(form);
      let variantId = formData.get('id') as string;

      if (!variantId) {
        const variantSelect = form.querySelector('select[name="id"]') as HTMLSelectElement;
        const variantInput = form.querySelector('input[name="id"]') as HTMLInputElement;

        if (variantSelect) {
          variantId = variantSelect.value;
        } else if (variantInput) {
          variantId = variantInput.value;
        }
      }

      const quantityInput = form.querySelector('input[name="quantity"]') as HTMLInputElement | null;
      const minQty = this.getMinQty(form);
      let quantity = quantityInput ? Number.parseInt(quantityInput.value, 10) : minQty;
      if (!Number.isFinite(quantity) || quantity < minQty) {
        quantity = minQty;
      }
      if (quantityInput) {
        quantityInput.min = String(minQty);
        quantityInput.value = String(quantity);
      }

      if (!variantId) {
        throw new Error(t('products.form.select_variant'));
      }

      if (quantity < 1) {
        throw new Error(t('products.form.quantity_minimum'));
      }

      await this.addToCart(variantId, quantity);

      if (isVariantPickerForm) {
        this.closeVariantPicker();
      }

      this.openCartDrawer();
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showError(form, (error as Error).message);
    } finally {
      this.setLoadingState(form, false);
    }
  }

  async addToCart(variantId: string, quantity: number): Promise<void> {
    try {
      (await ky
        .post('/cart/add.js', {
          json: {
            id: variantId,
            quantity: quantity,
          },
        })
        .json()) as AddToCartResponse;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        const kyError = error as { response: Response };
        const errorData: ErrorResponse = await kyError.response.json();
        throw new Error(errorData.message || errorData.description || t('products.form.add_to_cart_error'));
      }
      throw new Error(t('products.form.add_to_cart_error'));
    }

    await window.themeCart.refreshCart();
  }

  async openVariantPicker(handle: string): Promise<void> {
    const drawer = document.querySelector('#offcanvas-variant-picker') as HTMLElement;
    if (!drawer) {
      return;
    }

    try {
      const product = (await ky.get(`/products/${handle}.js`).json()) as ProductJson;
      this.populateVariantPicker(drawer, product);

      if (!this.variantPickerOffcanvas) {
        this.variantPickerOffcanvas = new Offcanvas(drawer);
      }

      this.variantPickerOffcanvas.show();
    } catch (error) {
      console.error('Error loading product variants:', error);
    }
  }

  populateVariantPicker(drawer: HTMLElement, product: ProductJson): void {
    const image = drawer.querySelector('.variant-picker-image') as HTMLImageElement;
    const title = drawer.querySelector('.variant-picker-title') as HTMLElement;
    const select = drawer.querySelector('.variant-selector') as HTMLSelectElement;
    const form = drawer.querySelector('.variant-picker-form form') as HTMLFormElement;

    if (product.featured_image) {
      image.src = product.featured_image;
      image.alt = product.title;
      image.hidden = false;
    } else {
      image.hidden = true;
    }

    title.textContent = product.title;

    select.innerHTML = '';
    product.variants.forEach((variant) => {
      const option = document.createElement('option');
      option.value = String(variant.id);
      option.textContent = this.buildVariantOptionLabel(variant);
      option.disabled = !variant.available;
      option.dataset.minQty = String(
        minQtyForOptions([variant.option1, variant.option2, variant.option3], variant.title),
      );
      option.dataset.price = this.formatMoney(variant.price);

      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        option.dataset.comparePrice = this.formatMoney(variant.compare_at_price);
      }

      select.appendChild(option);
    });

    const classicIndex = product.variants.findIndex((variant) => variant.title === 'Classic' && variant.available);
    const fallbackIndex = product.variants.findIndex((variant) => variant.available);
    select.selectedIndex = classicIndex >= 0 ? classicIndex : fallbackIndex >= 0 ? fallbackIndex : 0;

    if (form) {
      this.hideError(form);
    }

    this.handleVariantChange(select);
  }

  buildVariantOptionLabel(variant: ProductVariant): string {
    const price = this.formatMoney(variant.price);

    if (!variant.available) {
      return `${variant.title} - ${t('products.sold_out')}`;
    }

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      const comparePrice = this.formatMoney(variant.compare_at_price);
      return `${variant.title} - ${price} (${t('products.price.regular')} ${comparePrice})`;
    }

    return `${variant.title} - ${price}`;
  }

  formatMoney(cents: number): string {
    if (typeof window.Shopify !== 'undefined' && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }

    return new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(cents / 100);
  }

  closeVariantPicker(): void {
    const drawer = document.querySelector('#offcanvas-variant-picker') as HTMLElement;
    if (!drawer) {
      return;
    }

    const instance = Offcanvas.getInstance(drawer) || this.variantPickerOffcanvas;
    instance?.hide();
  }

  handleQuantityChange(button: HTMLElement, action: 'increase' | 'decrease'): void {
    const form = this.getFormRoot(button);
    if (!form) return;

    const input = form.querySelector('.quantity-input') as HTMLInputElement;
    if (!input) return;

    const currentQuantity = parseInt(input.value, 10) || 1;
    const minQuantity = parseInt(input.getAttribute('min') || '1', 10);
    const maxQuantity = parseInt(input.getAttribute('max') || '999', 10);

    let newQuantity: number;
    if (action === 'increase') {
      newQuantity = Math.min(currentQuantity + 1, maxQuantity);
    } else if (action === 'decrease') {
      newQuantity = Math.max(currentQuantity - 1, minQuantity);
    } else {
      return;
    }

    if (newQuantity !== currentQuantity) {
      input.value = newQuantity.toString();
      this.validateQuantityInput(input);
      this.refreshProductPrice(form);
    }
  }

  handleVariantChange(select: HTMLSelectElement): void {
    const form = this.getFormRoot(select);
    if (!form) return;

    const selectedOption = select.options[select.selectedIndex];
    if (!selectedOption) return;

    const isAvailable = !selectedOption.disabled;
    this.syncQuantityMinimum(form, minQtyFromDataset(selectedOption?.dataset.minQty));
    const addToCartBtn = form.querySelector('.add-to-cart-btn') as HTMLButtonElement;
    const quantityInput = form.querySelector('.quantity-input') as HTMLInputElement | null;

    if (!addToCartBtn) return;

    if (isAvailable) {
      addToCartBtn.disabled = false;
      addToCartBtn.textContent = addToCartBtn.dataset.addText || t('products.add_to_cart');

      if (quantityInput) {
        quantityInput.disabled = false;
        const maxQuantity = selectedOption.dataset.inventory || '999';
        quantityInput.setAttribute('max', maxQuantity);
      }
    } else {
      addToCartBtn.disabled = true;
      addToCartBtn.textContent = addToCartBtn.dataset.soldOutText || t('products.sold_out');

      if (quantityInput) {
        quantityInput.disabled = true;
      }
    }

    this.updatePriceDisplay(form, selectedOption);
  }

  getMinQty(form: HTMLFormElement): number {
    const select = form.querySelector('select[name="id"]') as HTMLSelectElement | null;
    if (select && select.options.length > 0) {
      return minQtyFromDataset(select.options[select.selectedIndex]?.dataset.minQty);
    }

    const hidden = form.querySelector('input[name="id"]') as HTMLInputElement | null;
    return minQtyFromDataset(hidden?.dataset.minQty);
  }

  syncQuantityMinimum(form: HTMLElement, minQty: number): void {
    const notice = form.querySelector('.quantity-min-notice') as HTMLElement | null;
    if (notice) {
      notice.hidden = minQty <= 1;
    }

    const quantityInput = form.querySelector('.quantity-input') as HTMLInputElement | null;
    if (!quantityInput) return;

    quantityInput.min = String(minQty);
    quantityInput.value = String(minQty);
  }

  refreshProductPrice(form: HTMLElement): void {
    const select = form.querySelector('.variant-selector') as HTMLSelectElement | null;
    if (select) {
      this.updatePriceDisplay(form, select.options[select.selectedIndex]);
      return;
    }

    this.updatePriceDisplay(form);
  }

  updatePriceDisplay(form: HTMLElement, selectedOption?: HTMLOptionElement): void {
    const variantPicker = form.closest('.offcanvas-variant-picker');
    if (variantPicker) {
      if (!selectedOption?.dataset.price) return;

      const priceContainer = variantPicker.querySelector('.variant-picker-price') as HTMLElement;
      if (!priceContainer) return;

      if (selectedOption.dataset.comparePrice) {
        priceContainer.innerHTML = `<span class="price-sale">${selectedOption.dataset.price}</span> <s class="price-compare">${selectedOption.dataset.comparePrice}</s>`;
      } else {
        priceContainer.textContent = selectedOption.dataset.price;
      }

      return;
    }

    const productContent = form.closest('.product-content') as HTMLElement;
    if (!productContent) return;

    const priceContainer = productContent.querySelector('.product-price') as HTMLElement;
    if (!priceContainer) return;

    const quantityInput = form.querySelector('.quantity-input') as HTMLInputElement | null;
    const qty = Math.max(1, parseInt(quantityInput?.value || '1') || 1);

    const priceCents = parseInt(selectedOption?.dataset.priceCents || priceContainer.dataset.priceCents || '', 10);
    if (Number.isNaN(priceCents)) return;

    const compareCents = parseInt(
      selectedOption?.dataset.comparePriceCents || priceContainer.dataset.comparePriceCents || '',
      10,
    );

    if (!Number.isNaN(compareCents) && compareCents > priceCents) {
      const savingsCents = (compareCents - priceCents) * qty;
      priceContainer.innerHTML = `
        <span class="price-sale">${this.formatMoney(priceCents * qty)}</span>
        <span class="price-compare">${this.formatMoney(compareCents * qty)}</span>
        <span class="price-savings">-${this.formatMoney(savingsCents)}</span>
      `;
    } else {
      priceContainer.innerHTML = `<span class="price-regular">${this.formatMoney(priceCents * qty)}</span>`;
    }

    priceContainer.dataset.priceCents = String(priceCents);
    if (!Number.isNaN(compareCents) && compareCents > priceCents) {
      priceContainer.dataset.comparePriceCents = String(compareCents);
    } else {
      delete priceContainer.dataset.comparePriceCents;
    }
  }

  validateQuantityInput(input: HTMLInputElement): void {
    const value = parseInt(input.value) || 1;
    const min = parseInt(input.getAttribute('min') || '1');
    const max = parseInt(input.getAttribute('max') || '999');

    if (value < min) {
      input.value = min.toString();
    } else if (value > max) {
      input.value = max.toString();
    }
  }

  setLoadingState(form: HTMLFormElement, isLoading: boolean): void {
    const addToCartBtn = form.querySelector('.add-to-cart-btn') as HTMLButtonElement;
    const quantityControls = form.querySelectorAll(
      '.qty-plus, .qty-minus, .quantity-input, .variant-selector',
    ) as NodeListOf<HTMLElement>;

    if (!addToCartBtn) return;

    if (isLoading) {
      addToCartBtn.disabled = true;
      addToCartBtn.dataset.originalText = addToCartBtn.textContent || '';
      addToCartBtn.textContent = t('products.form.adding');
      addToCartBtn.classList.add('loading');

      quantityControls.forEach((control) => {
        (control as HTMLInputElement | HTMLButtonElement | HTMLSelectElement).disabled = true;
      });
    } else {
      addToCartBtn.classList.remove('loading');
      addToCartBtn.textContent =
        addToCartBtn.dataset.originalText || addToCartBtn.dataset.addText || t('products.add_to_cart');

      quantityControls.forEach((control) => {
        (control as HTMLInputElement | HTMLButtonElement | HTMLSelectElement).disabled = false;
      });

      const variantSelect = form.querySelector('.variant-selector') as HTMLSelectElement;
      if (variantSelect) {
        this.handleVariantChange(variantSelect);
      } else {
        addToCartBtn.disabled = false;
      }
    }
  }

  showError(form: HTMLFormElement, message: string): void {
    const errorElement = form.querySelector('.product-form-error') as HTMLElement;
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.style.display = 'block';
      errorElement.className = 'product-form-error alert alert-danger';
    }
  }

  hideError(form: HTMLFormElement): void {
    const errorElement = form.querySelector('.product-form-error') as HTMLElement;
    if (errorElement) {
      errorElement.style.display = 'none';
    }
  }

  openCartDrawer(): void {
    const cartDrawer = document.querySelector('#offcanvas-cart') as HTMLElement;
    if (cartDrawer) {
      const offcanvas = new Offcanvas(cartDrawer);
      offcanvas.show();
    }
  }
}

export default ProductForm;
